import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  FilmStrip, Robot, Waveform, ShieldCheck, ArrowRight, CheckCircle, Play,
  ArrowsClockwise, MegaphoneSimple, Coins, Gift, Bug
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function QaCheckSection() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | busy | done | error
  const [errMsg, setErrMsg] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (state === 'busy' || !email.trim()) return;
    setState('busy');
    try {
      await axios.post(`${API_URL}/api/vcx/qa-interest`, { email: email.trim() });
      setState('done');
    } catch (err) {
      setErrMsg(err.response?.data?.detail || 'Something went wrong — email chip@migrationcrunch.com instead.');
      setState('error');
    }
  };
  return (
    <section className="py-14 border-t border-white/10" data-testid="vcx-qa-section">
      <p className="text-center text-[#F39C12] text-xs font-black uppercase tracking-[0.2em] mb-3">New — QA on camera</p>
      <h2 className="text-2xl sm:text-3xl font-black text-center mb-3">
        The robot that films your tutorials<br className="hidden sm:block" /> can also <span className="text-[#F39C12]">test your product.</span>
      </h2>
      <p className="text-sm text-white/60 text-center max-w-2xl mx-auto mb-10 leading-relaxed">
        Point the same camera at your signup, your checkout, your money path. If a robot that has
        never seen your product can&apos;t get through it — neither can your customers. And now you have the recording.
      </p>
      <div className="grid lg:grid-cols-2 gap-8 items-start max-w-4xl mx-auto">
        <div>
          <div className="bg-white/5 border border-[#F39C12]/40 rounded-xl p-5 mb-5" data-testid="vcx-qa-case-study">
            <p className="text-[10px] font-black uppercase tracking-wider text-[#F39C12] mb-2 flex items-center gap-1.5"><Bug size={13} weight="bold" /> True story — August 2026</p>
            <p className="text-sm text-white/80 leading-relaxed">
              Our own checkout had a bug no human caught: the email field rendered <span className="font-bold text-white">white text on a white box</span>.
              Every manual test &quot;passed&quot; — testers know what they typed. A paying customer found it.
              On a recording, it&apos;s obvious in a single frame.
              {' '}<Link to="/blog/the-bug-only-a-robot-could-see" className="text-[#F39C12] font-bold hover:underline" data-testid="vcx-qa-essay-link">Read the full story →</Link>
            </p>
          </div>
          <ul className="space-y-2.5 mb-6">
            {[
              'Walks your money path like a first-time customer — real clicks, real typing, real Stripe page',
              'Narrated recording of every run — a receipt you can watch, share, or send to your dev',
              'Re-run after every deploy: if the robot can\'t record it, you just caught a bug before your customers did',
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-white/70 leading-relaxed">
                <CheckCircle size={15} weight="fill" className="text-[#F39C12] shrink-0 mt-0.5" />{t}
              </li>
            ))}
          </ul>
          {state === 'done' ? (
            <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-lg px-4 py-3 text-sm text-emerald-300 font-bold" data-testid="vcx-qa-interest-done">
              You&apos;re on the list — we&apos;ll reach out when QA Checks open.
            </div>
          ) : (
            <form onSubmit={submit} className="flex gap-2" data-testid="vcx-qa-interest-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                className="flex-1 bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#F39C12]/60"
                data-testid="vcx-qa-interest-email"
              />
              <button
                type="submit"
                disabled={state === 'busy'}
                className="bg-[#F39C12] hover:bg-[#D68910] disabled:opacity-60 text-slate-900 font-black text-sm px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap"
                data-testid="vcx-qa-interest-submit"
              >
                {state === 'busy' ? 'Sending…' : 'Want this for your site?'}
              </button>
            </form>
          )}
          {state === 'error' && <p className="text-xs text-red-300 mt-2" data-testid="vcx-qa-interest-error">{errMsg}</p>}
          <p className="text-[11px] text-white/35 mt-2">No list, no drip sequence — we&apos;ll email you once when it&apos;s ready.</p>
        </div>
        <div className="rounded-xl overflow-hidden bg-white shadow-2xl ring-1 ring-white/25" data-testid="vcx-qa-video-card">
          <video controls preload="metadata" poster="/videocrunch-qa-checkout-poster.jpg"
                 className="w-full aspect-video bg-black block" data-testid="vcx-qa-video">
            <source src="/videocrunch-qa-checkout.mp4" type="video/mp4" />
          </video>
          <div className="p-3 text-slate-900">
            <p className="text-xs font-bold">Watch a QA Check run: the robot buys our own $599 migration tier — email gate, Stripe page, price verified — narrated end to end.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DemoVideo() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  return (
    <div className="rounded-xl overflow-hidden bg-white shadow-2xl ring-1 ring-white/25 max-w-3xl mx-auto" data-testid="vcx-demo-video-card">
      <div className="relative bg-slate-900 group">
        <video ref={videoRef} controls preload="metadata" poster="/videocrunch-poster-meta.jpg"
               className="w-full aspect-video bg-black block" data-testid="vcx-demo-video"
               onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}>
          <source src="/videocrunch-meta-demo.mp4" type="video/mp4" />
        </video>
        {!playing && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="absolute inset-0 bg-black/20" />
            <div className="relative w-20 h-20 rounded-full bg-[#F39C12] shadow-[0_8px_32px_rgba(0,0,0,0.55)] flex items-center justify-center group-hover:scale-110 transition-transform">
              <Play size={34} weight="fill" className="text-slate-900 ml-1" />
            </div>
          </div>
        )}
      </div>
      <div className="p-4 text-slate-900 flex items-center justify-between gap-4">
        <p className="text-sm font-bold">Watch VideoCrunch make a video — this walkthrough was itself produced by VideoCrunch.</p>
        <div className="flex items-center gap-3 shrink-0">
          <a href="https://www.youtube.com/watch?v=yVxxFg0lsJE" target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 text-xs font-black text-white bg-[#FF0000] hover:bg-[#CC0000] rounded-md px-3 py-1.5 transition-colors"
             data-testid="vcx-watch-on-youtube">
            <Play size={12} weight="fill" /> Watch on YouTube
          </a>
          <Link to="/tutorials" className="text-xs font-black text-[#0B5394] hover:underline" data-testid="vcx-see-all-link">More examples →</Link>
        </div>
      </div>
    </div>
  );
}

export default function VideoCrunchPage() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'VideoCrunch — AI tutorial videos that are never wrong';
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute('content');
    meta?.setAttribute('content', 'VideoCrunch drives a real browser through your software, narrates every step with a verified AI voice, and regenerates the video when your UI changes. First video free.');
    return () => { document.title = prevTitle; if (prevDesc) meta?.setAttribute('content', prevDesc); };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white" data-testid="vcx-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#F39C12] rounded flex items-center justify-center">
              <FilmStrip size={18} weight="bold" className="text-slate-900" />
            </div>
            <span className="text-lg font-black tracking-tight">Video<span className="text-[#F39C12]">Crunch</span></span>
          </div>
          <nav className="flex items-center gap-5 text-sm">
            <Link to="/voice" className="text-white/60 hover:text-white hidden sm:inline" data-testid="vcx-nav-voice">Free Voiceover Corrector</Link>
            <Link to="/videocrunch/app" className="text-white/60 hover:text-white hidden sm:inline" data-testid="vcx-nav-signin">Sign in</Link>
            <Link to="/videocrunch/app" className="bg-[#F39C12] hover:bg-[#D68910] text-slate-900 font-black text-sm px-4 py-2 rounded-lg transition-colors" data-testid="vcx-nav-cta">Start free</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6">
        {/* Hero */}
        <section className="pt-16 pb-12 text-center" data-testid="vcx-hero">
          <p className="text-[#F39C12] text-xs font-black uppercase tracking-[0.2em] mb-3">AI tutorial videos for your software</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4">
            Tutorial videos that are<br /><span className="text-[#F39C12]">never wrong.</span>
          </h1>
          <p className="text-sm text-white/70 max-w-2xl mx-auto leading-relaxed mb-8">
            VideoCrunch drives a real browser through your actual product, narrates every step with a
            human-cadence AI voice, and then <em>listens to its own narration</em> to prove every word
            was spoken correctly. When your UI changes, the videos re-record themselves. No camera.
            No actor. No editor. No re-shoots.
          </p>
          <div className="flex items-center justify-center gap-3 mb-12">
            <Link to="/videocrunch/app" className="inline-flex items-center gap-2 bg-[#F39C12] hover:bg-[#D68910] text-slate-900 font-black text-sm px-6 py-3 rounded-lg transition-colors" data-testid="vcx-hero-cta">
              Make my first video — free <ArrowRight size={15} weight="bold" />
            </Link>
            <Link to="/tutorials" className="inline-flex items-center gap-2 border border-white/25 hover:border-white/50 text-white/80 font-bold text-sm px-5 py-3 rounded-lg transition-colors" data-testid="vcx-hero-examples">
              <Play size={14} weight="fill" />Watch real examples
            </Link>
          </div>
          <DemoVideo />
        </section>

        {/* How it works */}
        <section className="py-14 border-t border-white/10" data-testid="vcx-how-section">
          <h2 className="text-lg font-bold text-center mb-10">How a VideoCrunch video gets made</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Robot, t: 'A robot uses your real product', d: 'Playwright drives an actual browser through your app — clicking, typing, scrolling. Pixel-perfect, every take.' },
              { icon: Waveform, t: 'An AI narrates like a presenter', d: 'Our CadenceScript engine paces the voice like a human — beats between list items, a pause before the payoff.' },
              { icon: ShieldCheck, t: 'Robot ears verify every word', d: 'The system transcribes its own narration and proves it matches the script. Misreads are caught and re-recorded automatically. Patent-pending.' },
              { icon: ArrowsClockwise, t: 'UI changed? Videos re-record', d: 'Your tutorials are scripts, not tapes. Ship a redesign and the whole library regenerates — no re-shoots, ever.' },
            ].map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/15 rounded-xl p-5">
                <s.icon size={24} weight="bold" className="text-[#F39C12] mb-3" />
                <h3 className="text-sm font-bold mb-1.5">{s.t}</h3>
                <p className="text-xs text-white/60 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-white/40 mt-8">
            Proof: every tutorial on <Link to="/tutorials" className="text-[#F39C12] hover:underline">SalesCrunchCRM</Link> — all
            26 of them — was produced this way. They also run as the CRM's automated test suite: if a video can't record, the app is broken.
          </p>
        </section>

        {/* Pricing — the credits model */}
        <section className="py-14 border-t border-white/10" data-testid="vcx-pricing">
          <h2 className="text-lg font-bold text-center mb-2">Simple pricing — pay in credits</h2>
          <p className="text-center text-xs text-white/50 mb-10">10 credits = 1 minute of finished video, billed to the second. Purchased credits never expire.</p>
          <div className="grid sm:grid-cols-3 gap-5 max-w-4xl mx-auto items-stretch">
            <div className="bg-white/5 border border-emerald-500/40 rounded-2xl p-6 text-center flex flex-col" data-testid="vcx-price-free">
              <Gift size={26} weight="bold" className="text-emerald-400 mx-auto mb-3" />
              <div className="text-3xl font-black mb-1">Free</div>
              <p className="text-xs text-white/60 mb-4">Your first video, up to 2 minutes. No card required. Carries a small "Made with VideoCrunch" mark — paid videos are clean.</p>
              <div className="mt-auto">
                <Link to="/videocrunch/app" className="block w-full border border-emerald-500/50 hover:bg-emerald-500/10 text-emerald-300 font-black text-sm px-4 py-2.5 rounded-lg transition-colors">Start free</Link>
              </div>
            </div>
            <div className="bg-white/5 border-2 border-[#F39C12]/60 rounded-2xl p-6 text-center relative flex flex-col" data-testid="vcx-price-sub">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F39C12] text-slate-900 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap">Best value</span>
              <Coins size={26} weight="bold" className="text-[#F39C12] mx-auto mb-3" />
              <div className="text-3xl font-black mb-1">$49<span className="text-base text-white/50 font-bold">/mo</span></div>
              <p className="text-xs text-white/60 mb-4">50 credits deposited every month — 5 minutes of video. Cancel anytime.</p>
              <div className="mt-auto">
                <Link to="/videocrunch/app" className="block w-full bg-[#F39C12] hover:bg-[#D68910] text-slate-900 font-black text-sm px-4 py-2.5 rounded-lg transition-colors">Subscribe</Link>
              </div>
            </div>
            <div className="bg-white/5 border border-white/15 rounded-2xl p-6 text-center flex flex-col" data-testid="vcx-price-packs">
              <Coins size={26} weight="bold" className="text-white/60 mx-auto mb-3" />
              <div className="text-3xl font-black mb-1">Credit packs</div>
              <p className="text-xs text-white/60 mb-4">Need more? Buy as you go:<br />20 for $20 · 50 for $50 · 100 for $100</p>
              <div className="mt-auto">
                <Link to="/videocrunch/app" className="block w-full border border-white/25 hover:border-white/50 text-white/80 font-black text-sm px-4 py-2.5 rounded-lg transition-colors">Buy credits</Link>
              </div>
            </div>
          </div>
        </section>

        {/* QA on camera */}
        <QaCheckSection />

        {/* FAQ */}
        <section className="py-14 border-t border-white/10" data-testid="vcx-faq">
          <h2 className="text-lg font-bold text-center mb-8">Questions people ask</h2>
          <div className="max-w-2xl mx-auto space-y-5">
            {[
              { q: 'How does it work with MY software?', a: 'Sign up, paste your app URL and a demo login (a staging URL works great), and describe the walkthrough in plain English. The robot records it on your real UI and the finished video appears in your dashboard.' },
              { q: 'How do credits work?', a: '10 credits = 1 minute of finished video, billed to the second — a 90-second video costs exactly 15 credits. When you queue a video we reserve the estimate, and refund every unused second when it publishes.' },
              { q: 'What happens when my UI changes?', a: 'That\'s the whole point — your videos are scripts, not tapes. Re-run them against the new UI and the library regenerates. Re-records just use credits like any video.' },
              { q: 'How do I know the narration is right?', a: 'Our patent-pending verification loop transcribes the AI voice back to text and machine-compares it to the script. A wrong word gets caught and re-recorded before you ever hear it.' },
              { q: 'Can I hear the voice quality first?', a: 'Yes — every tutorial on this site was narrated by it. Or paste your own script into the free Voiceover Corrector and preview the before/after.' },
            ].map((f, i) => (
              <div key={i} className="bg-white/5 border border-white/15 rounded-xl p-5">
                <h3 className="text-sm font-bold mb-1.5">{f.q}</h3>
                <p className="text-xs text-white/60 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-14 border-t border-white/10 text-center" data-testid="vcx-final-cta">
          <h2 className="text-2xl font-black mb-3">Your first video is on us.</h2>
          <p className="text-sm text-white/60 mb-6">Up to 2 minutes, narrated and verified — see your own product in a VideoCrunch tutorial.</p>
          <Link to="/videocrunch/app" className="inline-flex items-center gap-2 bg-[#F39C12] hover:bg-[#D68910] text-slate-900 font-black text-sm px-8 py-3.5 rounded-lg transition-colors" data-testid="vcx-final-cta-btn">
            Make my free video <ArrowRight size={15} weight="bold" />
          </Link>
        </section>

        {/* Footer */}
        <section className="py-10 border-t border-white/10 text-center">
          <p className="text-xs text-white/40 mb-3">
            VideoCrunch is built by the team behind <Link to="/" className="text-white/70 font-bold hover:text-white">SalesCrunchCRM</Link> — and it produced every tutorial video on this site.
          </p>
          <Link to="/voice" className="inline-flex items-center gap-2 text-[#F39C12] hover:text-amber-300 text-sm font-bold" data-testid="vcx-footer-voice-link">
            <MegaphoneSimple size={15} weight="bold" />Try the free Voiceover Corrector <ArrowRight size={13} weight="bold" />
          </Link>
        </section>
      </main>
    </div>
  );
}
